/**
 * External Imports
 */
import { FC, useEffect, useState, Dispatch, SetStateAction } from 'react';
import { useParams } from 'react-router-dom';

/**
 * Nexus Imports
 */
import { NeuNavVertical } from '@neutron/react';
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import { ChartLegend, CircPieChart } from '../metricsChart';
import { logListClick } from '../../utils/analyticsHelpers';

/**
 * Style Imports
 */
import { SideNavLabel } from './SideNav.styles';

interface EmployeeSideNavProps {
  completeCount: number;
  completePercent: number;
  todoCount: number;
  todoPercent: number;
  totalCount: number;
  unableCount?: number;
  newHires?: number;
  selectedTab: string;
  setSelectedTab: Dispatch<SetStateAction<string>>;
}

const EmployeeSideNav: FC<EmployeeSideNavProps> = ({
  completeCount,
  completePercent,
  todoCount,
  todoPercent,
  totalCount,
  newHires,
  unableCount,
  selectedTab,
  setSelectedTab
}) => {
  const [data, setData] = useState<number[]>([]);

  const { logTrackingEvent } = useAnalyticsApi();

  const { '*': view } = useParams();

  const changeTab = (tab: string) => {
    switch (tab) {
      case 'All':
        logTrackingEvent(logListClick('all', view));
        break;
      case 'To Do':
        logTrackingEvent(logListClick('to-do', view));
        break;
      case 'Completed':
        logTrackingEvent(logListClick('completed', view));
        break;
      case 'New Hires':
        logTrackingEvent(logListClick('new-hires', view));
        break;
      default:
        console.log('No event for tab filter');
    }
    setSelectedTab(tab);
  };

  useEffect(() => {
    if (unableCount !== undefined) {
      setData([completeCount, todoCount, unableCount || 0]);
    } else {
      setData([completeCount, todoCount]);
    }
  }, [completeCount, todoCount, unableCount]);

  return (
    <NeuNavVertical
      id="Side-Nav"
      className="h-auto"
      kind="outlined"
      label="MENU"
      menu="blue"
      mode="drawer"
      style={{
        boxShadow: 'rgba(0, 0, 0, 0.15) 0 2px 4px',
        '--nav-width-drawer': 'clamp(200px, 25vw, 200px)'
      }}
    >
      <NeuNavVertical
        unit="header"
        style={{
          padding:
            '0 calc(var(--nav-padding-x) / 1.5) calc(var(--nav-padding-y) / 0.75)'
        }}
      />
      <NeuNavVertical unit="content" style={{ overflow: 'clip' }}>
        <NeuNavVertical
          id="Side-Nav-All-Label"
          data-testid="side-nav-all-label"
          className="ps-2"
          active={selectedTab === 'All'}
          unit="item"
          onClick={() => changeTab('All')}
        >
          <SideNavLabel>HCA Colleagues ({totalCount})</SideNavLabel>
        </NeuNavVertical>
        <NeuNavVertical
          id="Side-Nav-To-Do-Label"
          data-testid="side-nav-to-do-label"
          className="ps-2"
          active={selectedTab === 'To Do'}
          unit="item"
          onClick={() => changeTab('To Do')}
        >
          <SideNavLabel>To Do ({todoCount})</SideNavLabel>
        </NeuNavVertical>
        <NeuNavVertical
          id="Side-Nav-Completed-Label"
          data-testid="side-nav-completed-label"
          className="ps-2"
          active={selectedTab === 'Completed'}
          unit="item"
          onClick={() => changeTab('Completed')}
        >
          <SideNavLabel>Completed ({completeCount})</SideNavLabel>
        </NeuNavVertical>
        <NeuNavVertical
          id="Side-Nav-New-Hire-Label"
          data-testid="side-nav-new-hire-label"
          className="ps-2"
          active={selectedTab === 'New Hires'}
          unit="item"
          onClick={() => changeTab('New Hires')}
        >
          <SideNavLabel>New Hires ({newHires})</SideNavLabel>
        </NeuNavVertical>
        {/* TODO: Comment out until we have contractors logic */}
        {/* <NeuNavVertical
        id="Side-Nav-Contractors-Label"
        active={selectedTab === 'Contractors'}
        unit="item"
        onClick={() => changeTab('Contractors')}
      >
       
        <SideNavLabel>Contractors ({todoCount})</SideNavLabel>
      </NeuNavVertical> */}
        <div className="d-flex flex-column px-0 mx-0">
          {/* {selectedTab !== 'Contractors' ? (
            <> */}
          {/* dont draw the census metrics chart if no data */}
          {data.length > 0 && <CircPieChart data={data} />}
          {data.length > 0 && (
            <ChartLegend
              completeCount={completeCount}
              completePercent={completePercent}
              todoCount={todoCount}
              todoPercent={todoPercent}
            />
          )}
          {/* </>
          ) : (
            ''
          )} */}
        </div>
      </NeuNavVertical>
    </NeuNavVertical>
  );
};

export default EmployeeSideNav;
